import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { FaGoogle, FaFacebook, FaMobile } from 'react-icons/fa'
import { useAuth } from '../context/AuthContext'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import authService from '../services/authService'
import SEO from '../utils/SEO'
import { trackLogin } from '../utils/analytics'
import Button from '../components/ui/Button'

const Login = () => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  })
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await login(formData)
      trackLogin('email')
      toast.success('Login successful!')

      // Check for redirect parameter
      const redirect = searchParams.get('redirect')
      if (redirect) {
        navigate(`/${redirect}`)
      } else {
        navigate('/')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = () => {
    authService.googleAuth()
  }

  const handleFacebookLogin = () => {
    authService.facebookAuth()
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEO title="Login" noindex />
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-display font-bold text-gradient">
            Welcome Back to Himachal Yatra
          </h2>
          <p className="mt-2 text-gray-600">Sign in to continue your journey</p>
        </div>

        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-8">
          {/* Social Login */}
          <div className="space-y-3 mb-6">
            <Button
              variant="outline"
              fullWidth
              icon={<FaGoogle className="text-red-500" size={20} />}
              onClick={handleGoogleLogin}
            >
              Continue with Google
            </Button>
            <Button
              variant="primary"
              fullWidth
              icon={<FaFacebook size={20} />}
              onClick={handleFacebookLogin}
            >
              Continue with Facebook
            </Button>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">
                <FaMobile className="inline mr-2" />
                Mobile Number or Email
              </label>
              <input
                type="text"
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                placeholder="Enter mobile number or email"
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="label">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className="input-field"
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-600">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Forgot password?
              </Link>
            </div>

            <Button type="submit" fullWidth disabled={loading} loading={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link
                to={
                  searchParams.get('redirect')
                    ? `/register?redirect=${searchParams.get('redirect')}`
                    : '/register'
                }
                className="link-primary"
              >
                Sign Up
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Admin users: Use mobile number login</p>
        </div>
      </div>
    </div>
  )
}

export default Login
